<template>
  <div class="pi-patterns">
    <div v-if="!view.maturity.ready" class="pi-patterns__notice" role="status">
      <p class="pi-patterns__notice-title">{{ view.maturity.text }}</p>
      <p v-if="view.maturity.reasonText" class="pi-patterns__notice-text">{{ view.maturity.reasonText }}</p>
      <p v-if="view.windowText" class="pi-patterns__notice-text">{{ view.windowText }}</p>
    </div>

    <template v-else>
      <p v-if="metaText" class="pi-muted pi-patterns__meta">{{ metaText }}</p>
      <p class="pi-muted pi-patterns__meta">{{ view.metricText }}</p>

      <div class="pi-patterns__head">
        <h3 class="pi-subtitle pi-patterns__title">Distribuição dos grupos</h3>
        <pi-export-menu title="Grupos de uso" :screen="exportScreen" :columns="groupColumns" :rows="view.groups"
                        :envelope="envelope" :filters="exportLines" :period="exportPeriod" :disabled="!view.groups.length"/>
      </div>
      <div v-if="view.groups.length" class="pi-table-wrap">
        <table class="pi-table">
          <caption class="pi-sr">Empresas por grupo de uso</caption>
          <thead>
          <tr>
            <pi-th v-for="column in groupColumns" :key="column.key" :column="column"/>
          </tr>
          </thead>
          <tbody>
          <tr v-for="group in view.groups" :key="group.key">
            <th scope="row" class="pi-patterns__group-cell">
              {{ group.label }}
              <span v-if="group.description" class="pi-table__sub">{{ group.description }}</span>
            </th>
            <td class="is-num">{{ group.companiesText }}</td>
            <td class="is-num">{{ group.shareText }}</td>
            <td class="is-num">{{ group.medianUserShareText }}</td>
            <td class="is-num">{{ group.medianActiveDaysText }}</td>
            <td v-if="view.hasPartialActions" class="is-num">{{ group.partialActionsText }}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="pi-muted">Nenhuma empresa elegível para agrupar nesta janela.</p>
      <p v-if="view.hasPartialActions" class="pi-muted pi-patterns__note">{{ partialActionsText }}</p>
      <p v-else-if="view.partialActionsNote" class="pi-muted pi-patterns__note">{{ view.partialActionsNote }}</p>

      <div class="pi-patterns__head pi-patterns__sub">
        <h3 class="pi-subtitle pi-patterns__title">Comparação de atributos por grupo</h3>
        <pi-export-menu title="Atributos por grupo" :screen="exportScreen" :columns="comparisonColumns" :rows="comparisonRows"
                        :envelope="envelope" :filters="exportLines" :period="exportPeriod" :disabled="!comparisonRows.length"/>
      </div>
      <p class="pi-patterns__association">{{ associationText }}</p>
      <p v-if="view.comparison.length && view.smallSampleText" class="pi-patterns__association">{{ view.smallSampleText }}</p>
      <template v-if="view.comparison.length">
        <div v-for="block in view.comparison" :key="block.key" class="pi-table-wrap pi-patterns__block">
          <table class="pi-table">
            <caption class="pi-patterns__caption">{{ block.label }} (% das empresas de cada grupo)</caption>
            <thead>
            <tr>
              <pi-th :column="valueColumn" :label="block.label"/>
              <pi-th v-for="column in view.columns" :key="column.key" :column="groupShareColumn" :label="column.label">
                {{ column.label }}
                <span v-if="column.noteText" class="pi-table__sub">{{ column.noteText }}</span>
              </pi-th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="row in block.rows" :key="row.key">
              <th scope="row" class="pi-patterns__group-cell">{{ row.label }}</th>
              <td v-for="cell in row.cells" :key="cell.key" class="is-num">{{ cell.text }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </template>
      <p v-else class="pi-muted">Sem atributos para comparar nesta janela.</p>

      <div class="pi-patterns__head pi-patterns__sub">
        <h3 class="pi-subtitle pi-patterns__title">Fluxos de duas etapas</h3>
        <pi-export-menu title="Fluxos de duas etapas" :screen="exportScreen" :columns="flowColumns" :rows="view.flows"
                        :envelope="envelope" :filters="exportLines" :period="exportPeriod" :disabled="!view.flows.length"/>
      </div>
      <div v-if="view.flows.length" class="pi-table-wrap">
        <table class="pi-table">
          <caption class="pi-sr">Conclusão dos fluxos de duas etapas</caption>
          <thead>
          <tr>
            <pi-th v-for="column in flowHeader" :key="column.key" :column="column"/>
          </tr>
          </thead>
          <tbody>
          <tr v-for="flow in view.flows" :key="flow.key">
            <th scope="row" class="pi-patterns__group-cell">
              {{ flow.label }}
              <span class="pi-table__sub">{{ flow.eventsText }}</span>
            </th>
            <td class="is-num">{{ flow.companiesText }}</td>
            <td class="is-num">{{ flow.medianText }}</td>
            <td class="is-num">{{ flow.belowHalfText }}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="pi-muted">Sem fluxo de duas etapas medido para esta funcionalidade.</p>

      <div class="pi-patterns__head pi-patterns__sub">
        <h3 class="pi-subtitle pi-patterns__title">Cobertura de papéis</h3>
        <pi-export-menu title="Cobertura de papéis" :screen="exportScreen" :columns="roleColumns" :rows="view.roleCoverage"
                        :envelope="envelope" :filters="exportLines" :period="exportPeriod" :disabled="!view.roleCoverage.length"/>
      </div>
      <div v-if="view.roleCoverage.length" class="pi-table-wrap">
        <table class="pi-table">
          <caption class="pi-sr">Papéis que aparecem nas empresas que usam</caption>
          <thead>
          <tr>
            <pi-th v-for="column in roleHeader" :key="column.key" :column="column"/>
          </tr>
          </thead>
          <tbody>
          <tr v-for="row in view.roleCoverage" :key="row.key">
            <th scope="row" class="pi-patterns__group-cell">{{ row.label }}</th>
            <td>{{ row.expectedText }}</td>
            <td class="is-num">{{ row.shareText }}<span v-if="row.baseText" class="pi-table__sub">{{ row.baseText }}</span></td>
          </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="pi-muted">Sem cobertura de papéis nesta janela.</p>

      <h3 class="pi-subtitle pi-patterns__sub">Exemplos por grupo</h3>
      <div v-if="view.examples.length" class="pi-patterns__examples">
        <div v-for="block in view.examples" :key="block.key" class="pi-patterns__example">
          <h4 class="pi-patterns__example-title">{{ block.label }}</h4>
          <ul class="pi-patterns__companies">
            <li v-for="company in block.companies" :key="company.key">
              <pi-company-name :company-id="company.companyId" :shard-code="company.shardCode" :name="company.name" :query="query"/>
              <span v-if="company.sizeBandText" class="pi-patterns__size">{{ company.sizeBandText }}</span>
            </li>
          </ul>
        </div>
      </div>
      <p v-else class="pi-muted">Sem exemplos nesta janela.</p>
    </template>

    <div class="pi-patterns__limits">
      <h3 class="pi-subtitle">Limitações</h3>
      <ul v-if="view.limitations.length" class="pi-patterns__limit-list">
        <li v-for="(item, index) in view.limitations" :key="index">{{ item }}</li>
      </ul>
      <p v-else class="pi-muted">Nenhuma limitação informada para esta janela.</p>
    </div>
  </div>
</template>

<script>
import piTh from "./piTh.vue";
import piCompanyName from "./piCompanyName.vue";
import piExportMenu from "./piExportMenu.vue";
import { columnByKey, screenColumns } from "./piColumns.js";
import {
  ASSOCIATION_TEXT, PARTIAL_ACTIONS_TEXT, PATTERN_COMPARISON_COLUMNS, PATTERN_FLOW_COLUMNS, PATTERN_ROLE_COLUMNS, comparisonRows, patternComparisonColumns,
  patternGroupColumns, patternLines,
} from "./piPatternsModel.js";

export default {
  name: "pi-usage-patterns",
  components: { piTh, piCompanyName, piExportMenu },
  props: {
    view: { type: Object, required: true },
    envelope: { type: Object, default: null },
    exportScreen: { type: String, default: "Funcionalidade" },
    exportFilters: { type: Array, default: () => [] },
    exportPeriod: { type: Object, default: null },
    query: { type: Object, default: null },
  },
  data() {
    return {
      associationText: ASSOCIATION_TEXT,
      partialActionsText: PARTIAL_ACTIONS_TEXT,
      flowColumns: PATTERN_FLOW_COLUMNS,
      flowHeader: screenColumns(PATTERN_FLOW_COLUMNS),
      roleColumns: PATTERN_ROLE_COLUMNS,
      roleHeader: screenColumns(PATTERN_ROLE_COLUMNS),
      valueColumn: columnByKey(PATTERN_COMPARISON_COLUMNS, "label"),
      groupShareColumn: columnByKey(PATTERN_COMPARISON_COLUMNS, "recurring"),
    };
  },
  computed: {
    metaText() {
      return [this.view.windowText, this.view.cadenceText, this.view.populationText].filter(Boolean).join(" · ");
    },
    groupColumns() {
      return patternGroupColumns(this.view);
    },
    comparisonColumns() {
      return patternComparisonColumns(this.view);
    },
    comparisonRows() {
      return comparisonRows(this.view.comparison);
    },
    exportLines() {
      return this.exportFilters.concat(patternLines(this.view));
    },
  },
};
</script>

<style src="./pi.css"></style>

<style scoped>
.pi-patterns__meta {
  margin-bottom: 6px;
}

.pi-patterns__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin-bottom: 8px;
}

.pi-patterns__title {
  margin: 0;
}

.pi-patterns__sub {
  margin-top: 18px;
}

.pi-patterns__note {
  margin-top: 6px;
}

.pi-patterns__group-cell {
  font-weight: 600;
  color: #1f2933;
}

.pi-patterns__association {
  margin: 0 0 8px;
  padding: 6px 10px;
  border-left: 3px solid #c5cad3;
  background: #fafbfc;
  font-size: 12px;
  color: #3a424e;
}

.pi-patterns__block {
  margin-bottom: 12px;
}

.pi-patterns__caption {
  caption-side: top;
  padding: 0 0 4px;
  font-size: 12px;
  font-weight: 700;
  color: #3a424e;
  text-align: left;
}

.pi-patterns__notice {
  margin: 0 0 12px;
  padding: 14px 16px;
  border: 1px dashed #c5cad3;
  border-radius: 6px;
  background: #fafbfc;
}

.pi-patterns__notice-title {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 700;
  color: #3a424e;
}

.pi-patterns__notice-text {
  margin: 0 0 2px;
  font-size: 12px;
  color: #4e5560;
}

.pi-patterns__examples {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.pi-patterns__example-title {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 700;
  color: #3a424e;
}

.pi-patterns__companies {
  margin: 0;
  padding-left: 16px;
  font-size: 12px;
}

.pi-patterns__size {
  margin-left: 6px;
  font-size: 11px;
  color: #6b7280;
}

.pi-patterns__limits {
  margin-top: 18px;
}

.pi-patterns__limit-list {
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  color: #4e5560;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piUsagePatterns.vue